import React, { useContext } from 'react'

import Button from '@components/Content/Button/Button'

import { WoocommerceContext } from '@context/woocommerceContext'

import { CustomizableComponent } from '@contracts/components/customizableComponent'

import * as styles from './BasketDropdown.module.scss'

import Spinner from '@icons/spinner.svg'

interface Props extends CustomizableComponent {
	active: boolean
	setActive: React.Dispatch<React.SetStateAction<boolean>>
}

const BasketDropdown: React.FC<Props> = (props) => {
	const [cart] = useContext(WoocommerceContext).cart
	const [cartLoading] = React.useContext(WoocommerceContext).cartLoading
	const [justAdded] = React.useContext(WoocommerceContext).justAddedToCart
	const [hoveringDropdown, setHoveringDropdown] = React.useState<boolean>(false)

	const dropdownRef = React.useRef<HTMLDivElement>(null)

	const cartStyles = [styles.basketDropdown]

	if (props.active) {
		cartStyles.push(styles.active)
	}

	if (props.className) {
		cartStyles.push(props.className)
	}

	if (cartLoading) {
		cartStyles.push(styles.loading)
	}

	// Hover event for basket dropdown
	const handleDropdownEnter = () => setHoveringDropdown(true)
	const handleDropdownLeave = () => setHoveringDropdown(false)

	React.useEffect(() => {
		const basketDropdown = dropdownRef.current

		if (basketDropdown) {
			basketDropdown.addEventListener('mouseenter', () => handleDropdownEnter)
			basketDropdown.addEventListener('mouseleave', () => handleDropdownLeave)
		}

		return () => {
			if (basketDropdown) {
				basketDropdown.removeEventListener('mouseenter', () => handleDropdownEnter)
				basketDropdown.removeEventListener('mouseleave', () => handleDropdownLeave)
			}
		}
	})

	// Remove active state when justAdded ticks over and the user isn't hovering the dropdown
	React.useEffect(() => {
		if (!justAdded) {
			if (!hoveringDropdown) {
				props.setActive(false)
			}
		}
	}, [justAdded])

	const emptyBasket = (
		<div className={cartStyles.join(' ')} ref={dropdownRef}>
			<div className={styles.content}>
				<div className={styles.section} style={{ textAlign: 'center' }}>
					<p>Your basket is empty</p>
				</div>
			</div>
		</div>
	)

	if (cart === null || cart === undefined) {
		return null
	}

	if (cart) {
		const { itemCount, nodes } = cart.contents

		if (itemCount === 0) {
			return emptyBasket
		}

		return (
			<div className={cartStyles.join(' ')} ref={dropdownRef}>
				<div className={styles.content}>
					<div className={[styles.section, styles.intro].join(' ')}>
						<strong>My Basket:</strong> {`${itemCount} item(s)`}
					</div>

					{justAdded && (
						<div className={[styles.section, styles.strip].join(' ')}>
							Item successfully added to your basket!
						</div>
					)}

					<div className={[styles.section].join(' ')}>
						<ul className={styles.products}>
							{nodes.map((item) => {
								const { key, product, quantity, variation } = item
								return (
									<li className={styles.product} key={key}>
										<div className={styles.image}>
											<img src={product.node.image.sourceUrl} alt={product.node.name} />
										</div>

										<div className={styles.details}>
											<div className={styles.name}>{product.node.name}</div>

											{variation ? (
												<div className={styles.variations}>
													{variation.attributes.map((attr) => (
														<p key={attr.id} className={styles.variation}>
															<strong style={{ textTransform: 'capitalize' }}>
																{attr.name}
															</strong>
															: {attr.value}
														</p>
													))}
												</div>
											) : null}

											<div className={styles.quantity}>
												{quantity} x {product.node.price}
											</div>
										</div>
									</li>
								)
							})}
						</ul>
					</div>
					<div className={[styles.section, styles.total].join(' ')}>
						<div className={styles.label}>
							<strong>Subtotal:</strong>
						</div>
						<div className={styles.value}>{cart.total}</div>
					</div>
					<div className={[styles.section, styles.buttons].join(' ')}>
						<Button data="basket" type="hollow">
							View Basket
						</Button>
						<Button data="checkout" type="default">
							Checkout
						</Button>
					</div>
				</div>

				{cartLoading && <Spinner className={styles.spinner} />}
			</div>
		)
	}

	return emptyBasket
}

export default BasketDropdown
